import React, { useEffect } from 'react';

import { useRouter } from '../../application/Router';
import { useDictionary } from '../../hooks/useDictionary';
import SearchIcon from '../../icons/SearchIcon';
import { colors } from '../../styles/colors';
import { rem } from '../../styles/rem';
import theme from '../../styles/theme';
import { FontFamily, FontWeight } from '../../styles/typography';

type Props = {
  filter: string;
  onFilter: (value: string) => void;
  label?: string;
  placeholder?: string;
};

const bodyFontFamily = theme.fonts?.[FontFamily.Body] || FontFamily.Body;

function PartiesFiltering({ filter, onFilter, label, placeholder }: Props) {
  const { t } = useDictionary();
  const { location, replace } = useRouter();
  const searchLabel = label || t('parties.search');

  useEffect(() => {
    if (filter !== location.state?.filter) {
      // Debounce setting the location state, so we don't cause router rerenders on every input
      const callbackId = window.setTimeout(() => {
        replace(location.pathname + location.search, {
          ...location.state,
          filter,
        });
      }, 200);

      return () => window.clearTimeout(callbackId);
    }
  }, [location, replace, filter]);

  return (
    <div css={{ position: 'relative', display: 'flex', zIndex: 0 }}>
      <input
        type="search"
        name="search"
        aria-label={searchLabel}
        placeholder={placeholder || searchLabel}
        aria-controls="parties-list"
        autoComplete="off"
        value={filter}
        css={{
          flex: '1 1 auto',
          width: '100%',
          border: 'none',
          borderRadius: 80,
          boxShadow: `inset 0 0 0 1px ${colors.base.Border.Brand}`,
          /* type-body-sm */
          fontFamily: bodyFontFamily,
          fontWeight: FontWeight.Normal,
          fontSize: rem(16),
          lineHeight: 1.43,
          overflowWrap: 'break-word',
          color: colors.base.Text.PrimaryStatic,
          backgroundColor: colors.base.Background.White,
          appearance: 'none',
          WebkitAppearance: 'none',
          boxSizing: 'border-box',
          height: rem(48),
          paddingTop: rem(12),
          paddingBottom: rem(12),
          paddingLeft: rem(20),
          paddingRight: rem(52),
          outline: 'none',
          cursor: 'text',
          '::-webkit-search-cancel-button': { display: 'none' },
          '::-webkit-input-placeholder': {
            opacity: 1,
            color: colors.base.Text.Secondary,
            fontFamily: bodyFontFamily,
          },
          ':-ms-input-placeholder': {
            opacity: 1,
            color: colors.base.Text.Secondary,
            fontFamily: bodyFontFamily,
          },
          '::-moz-placeholder': {
            opacity: 1,
            color: colors.base.Text.Secondary,
            fontFamily: bodyFontFamily,
          },
          '::placeholder': {
            opacity: 1,
            color: colors.base.Text.Secondary,
            fontFamily: bodyFontFamily,
          },
          '&:focus': {
            zIndex: 1,
            boxShadow: `inset 0 0 0 1px ${colors.base.Border.Brand}`,
          },
        }}
        onChange={(e) => onFilter(e.target.value)}
      />
      <SearchIcon
        aria-hidden
        sx={{
          position: 'absolute',
          width: rem(20),
          height: rem(20),
          right: rem(18),
          top: '50%',
          transform: 'translateY(-50%)',
          color: colors.base.Icon.Brand,
          zIndex: 2,
        }}
      />
    </div>
  );
}

export default PartiesFiltering;
